/**
 * Tipos compartidos para el Official Viewer.
 *
 * `TemplateSectionMeta` es la metadata estructural de una sección
 * declarada en `template_versions.definition.sections[]` (Doc 06 §1.1).
 * `TemplateI18nSection` es la parte localizable que vive en
 * `definition.i18n.{locale}.sections[]`.
 *
 * El viewer cruza ambos por `section_id` (estable, no traducido) para
 * pintar título + content.
 */

/** Block types soportados por el viewer/editor.
 *
 * Filosofía (B.6.d.3.b'): primitivas mínimas + composición. Patrones
 * frecuentes (ADR, AC, etc.) se construyen combinando varios nodos
 * genéricos (`fixed_heading` repetible que contiene rich_text + listas
 * + tablas anidadas), no como tipos hardcodeados.
 *
 * Tipos disponibles hoy:
 *   - Texto: rich_text · plain_text · key_value_grid
 *   - Listas: bullet_list · numbered_list · checklist
 *   - Tabla: table (legacy genérica) · structured_table (schema tipado, Commit E)
 *   - Diagramas Mermaid: diagram (legacy) + sub-tipos en Commit E
 *   - Otros: code_block · structured (debug)
 *   - Contenido fijo: fixed_heading (contenedor) · fixed_block (callout)
 *
 * Faltantes (B.6.d.3.c, requieren B.7 Azure Blob):
 *   - image_upload, file_upload, embed_canon
 *
 * El viewer cae al placeholder honesto "tipo `<x>` no soportado todavía"
 * para tipos que no estén aquí.
 */
/** Lista de los block types Mermaid (incluyendo el legacy `diagram`).
 * Todos comparten renderer y editor — solo difieren en placeholder y
 * (futuro Phase D) en el system prompt que la IA usa para generar la
 * sintaxis correcta. */
export const MERMAID_BLOCK_TYPES = [
  'diagram', // legacy, deprecado en el picker pero sigue renderizando
  'mermaid_flowchart',
  'mermaid_sequence',
  'mermaid_use_case',
  'mermaid_activity',
  'mermaid_state',
  'mermaid_er',
  'mermaid_class',
  'mermaid_gantt',
  'mermaid_journey',
  'mermaid_mindmap',
  'mermaid_generic',
] as const

export type MermaidBlockType = (typeof MERMAID_BLOCK_TYPES)[number]

/** True si el tipo es una variante Mermaid (todas usan el mismo
 * renderer y editor — solo cambia el placeholder). */
export function isMermaidType(t: unknown): t is MermaidBlockType {
  return (
    typeof t === 'string' &&
    (MERMAID_BLOCK_TYPES as readonly string[]).includes(t)
  )
}

/** Placeholder de sintaxis idiomática por sub-type Mermaid, en el
 * textarea del editor runtime (DiagramEditor). Todas las sintaxis son
 * válidas en Mermaid v11. */
export const MERMAID_PLACEHOLDERS: Record<MermaidBlockType, string> = {
  diagram: 'flowchart LR\n  A[Inicio] --> B[Fin]',
  mermaid_flowchart:
    'flowchart LR\n  A[Inicio] --> B{¿Cond?}\n  B -->|sí| C[OK]\n  B -->|no| D[Retry]',
  mermaid_sequence:
    'sequenceDiagram\n  participant U as Usuario\n  participant A as Auth\n  U->>A: POST /login\n  A-->>U: 200 OK',
  mermaid_use_case:
    'flowchart TB\n  Usuario((Usuario))\n  uc1[Iniciar sesión]\n  uc2[Recuperar contraseña]\n  Usuario --- uc1\n  Usuario --- uc2',
  mermaid_activity:
    'flowchart TB\n  start((Inicio)) --> step1[Validar inputs]\n  step1 --> dec{¿OK?}\n  dec -->|sí| done((Fin))\n  dec -->|no| start',
  mermaid_state:
    'stateDiagram-v2\n  [*] --> Idle\n  Idle --> Active: start\n  Active --> Idle: stop\n  Active --> [*]: kill',
  mermaid_er:
    'erDiagram\n  USER ||--o{ POST : escribe\n  USER {\n    int id\n    string name\n  }\n  POST {\n    int id\n    string title\n  }',
  mermaid_class:
    'classDiagram\n  class Animal {\n    +String name\n    +eat()\n  }\n  Animal <|-- Dog\n  Animal <|-- Cat',
  mermaid_gantt:
    'gantt\n  title Cronograma\n  dateFormat YYYY-MM-DD\n  section Diseño\n    Tarea 1: 2026-01-01, 5d\n    Tarea 2: after Tarea 1, 3d',
  mermaid_journey:
    'journey\n  title User journey de onboarding\n  section Registro\n    Crear cuenta: 5: Usuario\n    Verificar email: 3: Usuario',
  mermaid_mindmap:
    'mindmap\n  root((idea))\n    Origen\n    Resultado\n      Sub-resultado',
  mermaid_generic:
    '%% Cualquier sintaxis Mermaid válida\nflowchart LR\n  A --> B',
}

export type SupportedBlockType =
  | 'rich_text'
  | 'plain_text'
  | 'bullet_list'
  | 'numbered_list'
  | 'checklist'
  | 'key_value_grid'
  | 'structured'
  | 'code_block'
  | 'fixed_heading'
  | 'fixed_block'
  | 'structured_table'
  | MermaidBlockType

/** True si el block type tiene render implementado. */
export function isSupportedBlockType(t: unknown): t is SupportedBlockType {
  return (
    t === 'rich_text' ||
    t === 'plain_text' ||
    t === 'bullet_list' ||
    t === 'numbered_list' ||
    t === 'checklist' ||
    t === 'key_value_grid' ||
    t === 'structured' ||
    t === 'code_block' ||
    t === 'fixed_heading' ||
    t === 'fixed_block' ||
    t === 'structured_table' ||
    isMermaidType(t)
  )
}

export interface TemplateSectionMeta {
  section_id: string
  type: string
  order: number
  required?: boolean
  /** Otros campos (validation, max_length, etc.) — preservamos como
   * Record para no perderlos pero el viewer no los renderiza por ahora. */
  [extra: string]: unknown
}

export interface TemplateI18nSection {
  section_id: string
  title: string
  /** Texto fijo del callout para `fixed_block`. */
  body?: string
  /** Texto en gris dentro del input cuando está vacío (hojas editables). */
  placeholder?: string
  /** Texto pequeño debajo del input explicando qué se espera. */
  help_text?: string
  ai_instructions?: Record<string, string>
  /** Mapa column_key → label localizado, para `structured_table`. */
  column_labels?: Record<string, string>
}

export interface TemplateI18nBlock {
  name: string
  description?: string
  sections: TemplateI18nSection[]
}

export interface TemplateDefinition {
  template_id?: string
  template_type?: string
  template_version?: string
  i18n?: {
    es?: TemplateI18nBlock
    en?: TemplateI18nBlock
  }
  sections: TemplateSectionMeta[]
  [extra: string]: unknown
}

/** Copy localizado de cada sección de la plantilla, por `section_id`. */
export function templateI18nSectionMap(
  templateDef: TemplateDefinition,
  locale: 'es' | 'en',
): Map<string, TemplateI18nSection> {
  const m = new Map<string, TemplateI18nSection>()
  for (const s of templateDef.i18n?.[locale]?.sections ?? []) m.set(s.section_id, s)
  return m
}

/** Título visible de una sección; en una instancia de repetible agrega su
 * clave para distinguirla de las demás. */
export function sectionInstanceTitle(
  sectionId: string,
  instanceKey: string,
  i18nMap: Map<string, TemplateI18nSection>,
): string {
  const base = i18nMap.get(sectionId)?.title ?? sectionId
  return instanceKey && instanceKey !== sectionId ? `${base} · ${instanceKey}` : base
}

// ─── Sprint AI-1.f: Path scoping para repetibles ───────────────────────────
/** Un segmento del path desde root al caller. Cuando `index` está
 * presente, indica que estamos dentro de la instancia índice `index` del
 * repetible cuyo array vive bajo `section_id` (convención HIVA: el
 * contenedor padre lleva el array, el repeatable singular es implícito).
 * Cuando `index` es undefined, es una hoja terminal (la sección activa).
 *
 * Ejemplo (caller = Diagrama del CU2 / FA4):
 *   [
 *     { section_id: "casos_de_uso", index: 1 },
 *     { section_id: "flujos_alternos", index: 3 },
 *     { section_id: "diagrama" }
 *   ]
 *
 * Containers transparentes (sin repeatable child) NO entran al path —
 * sus children aparecen flat en el content del padre.
 */
export interface PathSegment {
  section_id: string
  index?: number
}

/** Lee el value en `path` dentro de `content`. Devuelve `undefined` si
 * la ruta no existe (por ejemplo: instancia índice fuera de rango,
 * sección hoja sin escribir). */
export function getValueAtPath(
  content: Record<string, unknown> | null | undefined,
  path: PathSegment[],
): unknown {
  let cursor: unknown = content
  for (const seg of path) {
    if (cursor === null || cursor === undefined) return undefined
    if (typeof cursor !== 'object') return undefined
    const next = (cursor as Record<string, unknown>)[seg.section_id]
    if (seg.index !== undefined) {
      if (!Array.isArray(next)) return undefined
      cursor = next[seg.index]
    } else {
      cursor = next
    }
  }
  return cursor
}

/** Escribe `value` en `path` dentro de `content`. Devuelve un NUEVO
 * objeto (no muta el input) — necesario para que React detecte el cambio.
 * Crea segmentos intermedios faltantes (`{}` para hojas, `[]` para
 * arrays bajo containers con repeatable). Si un índice queda fuera de
 * rango del array, expande con `undefined` hasta llegar al índice
 * (defensivo — no debería pasar en flujo normal). */
export function setValueAtPath(
  content: Record<string, unknown> | null | undefined,
  path: PathSegment[],
  value: unknown,
): Record<string, unknown> {
  if (path.length === 0) {
    // Reemplazo total — caller raro pero válido.
    return (value ?? {}) as Record<string, unknown>
  }
  const root: Record<string, unknown> = { ...(content ?? {}) }
  let cursor: Record<string, unknown> = root
  for (let i = 0; i < path.length - 1; i++) {
    const seg = path[i]!
    if (seg.index !== undefined) {
      // Container con repeatable child: cursor[section_id] es un array.
      const arr = Array.isArray(cursor[seg.section_id])
        ? [...(cursor[seg.section_id] as unknown[])]
        : []
      while (arr.length <= seg.index) arr.push(undefined)
      const inst =
        arr[seg.index] && typeof arr[seg.index] === 'object'
          ? { ...(arr[seg.index] as Record<string, unknown>) }
          : {}
      arr[seg.index] = inst
      cursor[seg.section_id] = arr
      cursor = inst
    } else {
      // Sección intermedia non-leaf, non-repeatable. Raro en la
      // convención (containers transparentes no entran al path), pero
      // soportamos por robustez.
      const sub =
        cursor[seg.section_id] && typeof cursor[seg.section_id] === 'object'
          ? { ...(cursor[seg.section_id] as Record<string, unknown>) }
          : {}
      cursor[seg.section_id] = sub
      cursor = sub as Record<string, unknown>
    }
  }
  // Último segmento → write final.
  const last = path[path.length - 1]!
  if (last.index !== undefined) {
    const arr = Array.isArray(cursor[last.section_id])
      ? [...(cursor[last.section_id] as unknown[])]
      : []
    while (arr.length <= last.index) arr.push(undefined)
    arr[last.index] = value
    cursor[last.section_id] = arr
  } else {
    cursor[last.section_id] = value
  }
  return root
}

/** Agrega una instancia al final del array del repetible cuyo container
 * vive en `containerPath`. Devuelve un NUEVO content. */
export function appendInstance(
  content: Record<string, unknown> | null | undefined,
  containerPath: PathSegment[],
  newInstance: Record<string, unknown> = {},
): Record<string, unknown> {
  const current = getValueAtPath(content, containerPath)
  const arr = Array.isArray(current) ? current : []
  return setValueAtPath(content, containerPath, [...arr, newInstance])
}

/** Elimina la instancia de índice `index` del array bajo `containerPath`.
 * Devuelve un NUEVO content. No-op si el índice está fuera de rango. */
export function removeInstance(
  content: Record<string, unknown> | null | undefined,
  containerPath: PathSegment[],
  index: number,
): Record<string, unknown> {
  const current = getValueAtPath(content, containerPath)
  if (!Array.isArray(current)) return { ...(content ?? {}) }
  if (index < 0 || index >= current.length) return { ...(content ?? {}) }
  const next = current.slice()
  next.splice(index, 1)
  return setValueAtPath(content, containerPath, next)
}
